import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle } from '@/components/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { FormGroup } from '@/components/form/FormGroup';
import { Input } from '@/components/form/Input';
import { Textarea } from '@/components/form/Textarea';
import { SearchableSelect } from '@/components/form/SearchableSelect';
import { ItemsTable } from '@/components/form/ItemsTable';
import { CategoryItemPicker } from '@/components/form/CategoryItemPicker';
import { ItemThumb } from '@/components/ui/ItemThumb';
import { useToast } from '@/components/ui/useToast';
import { useFieldErrors } from '@/lib/useFieldErrors';
import { apiError } from '@/lib/apiError';
import { generatePoFromPr, type PoItem } from '@/api/po';
import { listPrs, type Pr } from '@/api/pr';
import { listParties, type Party } from '@/api/parties';
import { listItems } from '@/api/items';
import { formatINR, formatINRSymbol, formatDate, todayISO } from '@/lib/format';

interface Props {
  open: boolean;
  onClose: () => void;
}

type ItemMaster = { id: number; code: string; name: string; category: string; unit?: string; hsn?: string | null; gstPct?: string | number | null; purchaseRate?: string | number | null; imageUrl?: string | null; barcode?: string | null };

/** Item Master values for a PO line: HSN, GST % and the purchase cost as unit price. */
function masterValues(m: ItemMaster | undefined) {
  return {
    hsn: m?.hsn ?? '',
    gstPct: m?.gstPct != null && m.gstPct !== '' ? String(Number(m.gstPct)) : '0',
    purchaseRate: Number(m?.purchaseRate ?? 0),
  };
}

function linesFromPr(p: Pr, itemMaster: ItemMaster[]): PoItem[] {
  return p.items.map((it) => {
    const m = it.itemId ? itemMaster.find((i) => i.id === it.itemId) : undefined;
    const mv = masterValues(m);
    return {
      itemId: it.itemId ?? null,
      itemName: it.itemName,
      hsn: mv.hsn,
      qty: it.qty,
      unit: it.unit,
      // Unit price = the item's purchase cost from the Item Master; the PR's own price only if the master has none.
      unitPrice: mv.purchaseRate > 0 ? mv.purchaseRate : Number(it.unitPrice ?? 0),
      gstPct: mv.gstPct,
    };
  });
}

/**
 * Purchase-team fast-track: pick an HOD-approved PR and a vendor, review + edit
 * the lines (mirrors the New PO form), and turn it straight into a Draft PO —
 * skipping RFQ + Quote Approval. PO creation lives here (with the purchase team).
 */
export function PoFromPrModal({ open, onClose }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const fe = useFieldErrors<'prId' | 'vendorId'>();
  const [prId, setPrId] = useState('');
  const [vendorId, setVendorId] = useState('');
  const [poDate, setPoDate] = useState(todayISO());
  const [subject, setSubject] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [paymentTermDays, setPaymentTermDays] = useState('30');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [termsConditions, setTermsConditions] = useState('Standard terms apply.');
  const [items, setItems] = useState<PoItem[]>([]);

  // Show both Pending and Approved PRs. Only Approved ones can actually be
  // turned into a PO (backend-enforced); Pending appear for visibility and are
  // gated below with a prompt to approve first.
  const { data: apprResp } = useQuery({
    queryKey: ['pr', { status: 'Approved', pageSize: 500 }],
    queryFn: () => listPrs({ status: 'Approved', pageSize: 500 }),
    enabled: open,
  });
  const { data: pendResp } = useQuery({
    queryKey: ['pr', { status: 'Pending', pageSize: 500 }],
    queryFn: () => listPrs({ status: 'Pending', pageSize: 500 }),
    enabled: open,
  });
  const prs: Pr[] = useMemo(
    () =>
      [...(apprResp?.data ?? []), ...(pendResp?.data ?? [])].sort((a, b) =>
        b.prNo.localeCompare(a.prNo),
      ),
    [apprResp, pendResp],
  );
  const prOptions = useMemo(
    () =>
      prs.map((p) => ({
        value: p.id,
        label: p.subject ? `${p.prNo} — ${p.subject}` : p.prNo,
        sublabel: `${p.status} · ${p.department} · ${p.items.length} item(s) · ${formatINRSymbol(p.totalValue)}`,
      })),
    [prs],
  );
  const selectedPr = useMemo(() => prs.find((p) => p.id === Number(prId)) ?? null, [prs, prId]);
  const selectedApproved = selectedPr?.status === 'Approved';

  const { data: vendorsResp } = useQuery({
    queryKey: ['parties', { type: 'vendor', pageSize: 10000 }],
    queryFn: () => listParties({ type: 'vendor', pageSize: 10000 }),
    enabled: open,
  });
  const vendors: Party[] = useMemo(() => vendorsResp?.data ?? [], [vendorsResp]);
  const vendorOptions = useMemo(
    () => vendors.map((v) => ({ value: v.id, label: v.name, sublabel: v.gstin ?? undefined })),
    [vendors],
  );

  const { data: itemsResp } = useQuery({
    queryKey: ['items', { pageSize: 10000, status: 'Active' }],
    queryFn: () => listItems({ pageSize: 10000, status: 'Active' }),
    enabled: open,
    staleTime: 5 * 60_000,
  });
  const itemMaster = useMemo(() => (itemsResp?.data ?? []) as unknown as ItemMaster[], [itemsResp]);

  // If the Item Master arrives after the PR was picked, fill the lines' still-blank
  // HSN / unit price / GST % from it (never over a value already entered).
  useEffect(() => {
    if (!itemMaster.length) return;
    setItems((rows) => {
      let changed = false;
      const next = rows.map((r) => {
        const m = r.itemId != null ? itemMaster.find((i) => i.id === r.itemId) : undefined;
        if (!m) return r;
        const mv = masterValues(m);
        const patch: Partial<PoItem> = {};
        if (!String(r.hsn ?? '').trim() && mv.hsn) patch.hsn = mv.hsn;
        if (!Number(r.unitPrice || 0) && mv.purchaseRate > 0) patch.unitPrice = mv.purchaseRate;
        if (!Number(r.gstPct || 0) && Number(mv.gstPct) > 0) patch.gstPct = mv.gstPct;
        if (!Object.keys(patch).length) return r;
        changed = true;
        return { ...r, ...patch };
      });
      return changed ? next : rows;
    });
  }, [itemMaster]);

  const totals = useMemo(() => {
    let subtotal = 0;
    let gstAmount = 0;
    for (const it of items) {
      const line = Number(it.qty || 0) * Number(it.unitPrice || 0);
      subtotal += line;
      gstAmount += (line * Number(it.gstPct || 0)) / 100;
    }
    return {
      subtotal: Math.round(subtotal * 100) / 100,
      gstAmount: Math.round(gstAmount * 100) / 100,
      grandTotal: Math.round((subtotal + gstAmount) * 100) / 100,
    };
  }, [items]);

  function reset() {
    setPrId('');
    setVendorId('');
    setPoDate(todayISO());
    setSubject('');
    setDeliveryDate('');
    setPaymentTermDays('30');
    setDeliveryAddress('');
    setTermsConditions('Standard terms apply.');
    setItems([]);
    fe.clear();
  }

  function onPickPr(val: number | string | '') {
    const id = val ? String(val) : '';
    setPrId(id);
    fe.clear('prId');
    const picked = prs.find((p) => p.id === Number(id));
    setItems(picked ? linesFromPr(picked, itemMaster) : []);
    setSubject(picked?.subject ?? '');
    if (picked?.vendorId) { setVendorId(String(picked.vendorId)); fe.clear('vendorId'); }
  }

  const mutation = useMutation({
    mutationFn: () => {
      const payload = items
        .filter((it) => it.itemName.trim() && Number(it.qty) > 0 && it.unit.trim())
        .map((it) => ({
          itemId: it.itemId ?? undefined,
          itemName: it.itemName.trim(),
          hsn: (it.hsn ?? '').toString().trim() || undefined,
          qty: Number(it.qty),
          unit: it.unit.trim(),
          unitPrice: Number(it.unitPrice ?? 0),
          gstPct: it.gstPct != null && it.gstPct !== '' ? Number(it.gstPct) : 0,
        }));
      return generatePoFromPr(Number(prId), Number(vendorId), {
        poDate: poDate || undefined,
        subject: subject.trim(),
        deliveryDate: deliveryDate || undefined,
        paymentTermDays: paymentTermDays ? Number(paymentTermDays) : undefined,
        deliveryAddress: deliveryAddress.trim() || undefined,
        termsConditions: termsConditions.trim() || undefined,
        items: payload,
      });
    },
    onSuccess: (po) => {
      qc.invalidateQueries({ queryKey: ['pr'] });
      qc.invalidateQueries({ queryKey: ['po'] });
      toast.pushStep(`Draft PO ${po.poNo} created`, {
        body: 'Review the Draft PO, then send it to the vendor.',
        tag: `po-from-pr-${po.id}`,
      });
      reset();
      onClose();
    },
    onError: (e: unknown) => toast.push(apiError(e, 'Could not generate PO'), 'error'),
  });

  function submit() {
    if (!fe.validate({ prId: !prId && 'Select a PR', vendorId: !vendorId && 'Select a vendor' })) return;
    if (!selectedApproved) {
      toast.push('This PR is pending approval — approve it before generating a PO', 'error');
      return;
    }
    const valid = items.filter((it) => it.itemName.trim() && Number(it.qty) > 0 && it.unit.trim());
    if (valid.length === 0) {
      toast.push('Add at least one valid item with qty + unit', 'error');
      return;
    }
    mutation.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={() => { reset(); onClose(); }}
      title="New PO from PR"
      size="xl"
      footer={
        <>
          <Button variant="glass" onClick={() => { reset(); onClose(); }}>Cancel</Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={mutation.isPending || (!!selectedPr && !selectedApproved)}
            title={!!selectedPr && !selectedApproved ? 'This PR must be approved first' : undefined}
          >
            {mutation.isPending ? 'Generating…' : 'Generate PO'}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <FormGroup label="Approved PR" required error={fe.errors.prId}>
          <SearchableSelect
            options={prOptions}
            value={prId ? Number(prId) : ''}
            onChange={onPickPr}
            placeholder={prOptions.length ? 'Type to search approved PR…' : 'No approved PRs available'}
            className={fe.has('prId') ? 'rounded-lg ring-1 ring-danger' : undefined}
          />
        </FormGroup>
        <FormGroup label="Vendor" required error={fe.errors.vendorId}>
          <SearchableSelect
            options={vendorOptions}
            value={vendorId ? Number(vendorId) : ''}
            onChange={(val) => { setVendorId(val ? String(val) : ''); fe.clear('vendorId'); }}
            placeholder="Type to search vendor…"
            className={fe.has('vendorId') ? 'rounded-lg ring-1 ring-danger' : undefined}
          />
        </FormGroup>
        <FormGroup label="Subject" className="col-span-2">
          <Input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Copied from the PR — optional"
            maxLength={200}
          />
        </FormGroup>
        <FormGroup label="Date" required>
          <Input type="date" value={poDate} onChange={(e) => setPoDate(e.target.value)} />
        </FormGroup>
        <FormGroup label="Delivery Date">
          <Input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} />
        </FormGroup>
        <FormGroup label="Payment Terms (Days)">
          <Input type="number" value={paymentTermDays} onChange={(e) => setPaymentTermDays(e.target.value)} />
        </FormGroup>
        <FormGroup label="Delivery Address">
          <Input
            value={deliveryAddress}
            onChange={(e) => setDeliveryAddress(e.target.value)}
            placeholder="Defaults to company address"
          />
        </FormGroup>
        <FormGroup label="Terms & Conditions" className="col-span-2">
          <Textarea rows={2} value={termsConditions} onChange={(e) => setTermsConditions(e.target.value)} />
        </FormGroup>
      </div>

      {!selectedPr ? (
        <div className="mt-3 rounded-card border border-dashed border-[var(--border-color)] bg-[var(--bg-soft)] px-3 py-6 text-center text-xs text-[var(--text-muted)]">
          Select an approved PR to load and edit its items.
        </div>
      ) : (
        <>
          {!selectedApproved && (
            <div className="mt-3 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-[11px] font-semibold text-warning">
              <AlertTriangle size={13} className="mr-1 inline-block align-[-2px]" /> This PR is <span className="font-bold">Pending</span> — it must be approved on the PR Approval page
              before a PO can be generated. You can still review its items below.
            </div>
          )}

          {/* Source PR at a glance */}
          <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 rounded-card border border-[var(--border-color)] bg-[var(--bg-soft)] p-3 sm:grid-cols-4">
            <Meta label="PR #"><span className="font-extrabold text-accent">{selectedPr.prNo}</span></Meta>
            <Meta label="Date">{formatDate(selectedPr.prDate)}</Meta>
            <Meta label="Department">{selectedPr.department}</Meta>
            <Meta label="MRN Ref">{selectedPr.mrn?.mrnNo ?? '—'}</Meta>
            <Meta label="Authority">{selectedPr.authority}</Meta>
            <Meta label="Suggested Vendor">{selectedPr.vendor?.name ?? '—'}</Meta>
            <Meta label="Lines">{items.length}</Meta>
            <Meta label="PR Est. Value">{formatINRSymbol(selectedPr.totalValue)}</Meta>
            {selectedPr.remarks && (
              <div className="col-span-2 min-w-0 sm:col-span-4">
                <div className="text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">Remarks</div>
                <div className="text-xs text-[var(--text-primary)]">{selectedPr.remarks}</div>
              </div>
            )}
          </div>

          <ItemsTable<PoItem>
            title="PO Items"
            rows={items}
            onChange={setItems}
            newRow={() => ({ itemName: '', hsn: '', qty: '', unit: '', unitPrice: '', gstPct: '0' })}
            columns={[
              {
                key: 'image',
                header: 'Image',
                width: '52px',
                cell: (row) => {
                  const img = row.itemId != null ? (itemMaster.find((i) => i.id === row.itemId)?.imageUrl ?? null) : null;
                  return <ItemThumb src={img} size={34} />;
                },
              },
              {
                key: 'item',
                header: 'Item',
                cell: (row, _i, update) => (
                  <CategoryItemPicker
                    showImage
                    showSelectedThumb={false}
                    showBarcode={false}
                    items={itemMaster.map((it) => ({ id: it.id, code: it.code, name: it.name, category: it.category, currentStock: 0, imageUrl: it.imageUrl, barcode: it.barcode }))}
                    value={row.itemId ?? '__custom__'}
                    onCustom={() => update({ itemId: null })}
                    onPick={(id) => {
                      const it = itemMaster.find((i) => i.id === id);
                      update({
                        itemId: id,
                        itemName: it?.name ?? row.itemName,
                        unit: it?.unit ?? row.unit,
                        hsn: it?.hsn ?? row.hsn,
                        gstPct: it?.gstPct != null ? String(it.gstPct) : row.gstPct,
                        unitPrice: it ? Number(it.purchaseRate || 0) : row.unitPrice,
                      });
                    }}
                  />
                ),
              },
              { key: 'barcode', header: 'Barcode', width: '110px', cell: (row) => {
                const bc = row.itemId != null ? (itemMaster.find((i) => i.id === row.itemId)?.barcode ?? null) : null;
                return <span className="font-mono text-[11px] font-bold text-[var(--text-primary)]">{bc ?? '—'}</span>;
              } },
              { key: 'itemName', header: 'Description', cell: (row, _i, update) => (
                <Input value={row.itemName} onChange={(e) => update({ itemName: e.target.value })} />
              ) },
              { key: 'hsn', header: 'HSN', width: '90px', cell: (row, _i, update) => (
                <Input value={String(row.hsn ?? '')} onChange={(e) => update({ hsn: e.target.value })} />
              ) },
              { key: 'qty', header: 'Qty', width: '90px', align: 'right', cell: (row, _i, update) => (
                <Input type="number" value={String(row.qty ?? '')} onChange={(e) => update({ qty: e.target.value })} className="text-right" />
              ) },
              { key: 'unit', header: 'Unit', width: '90px', cell: (row, _i, update) => (
                <Input value={row.unit} onChange={(e) => update({ unit: e.target.value })} />
              ) },
              { key: 'unitPrice', header: 'Unit Price', width: '120px', align: 'right', cell: (row, _i, update) => (
                <Input type="number" step="0.01" value={String(row.unitPrice ?? '')} onChange={(e) => update({ unitPrice: e.target.value })} className="text-right" />
              ) },
              { key: 'gstPct', header: 'GST%', width: '80px', align: 'right', cell: (row, _i, update) => (
                <Input type="number" value={String(row.gstPct ?? '')} onChange={(e) => update({ gstPct: e.target.value })} className="text-right" />
              ) },
              { key: 'rowTotal', header: 'Total', width: '120px', align: 'right', cell: (row) => (
                <span className="font-bold text-accent">₹{formatINR(Number(row.qty || 0) * Number(row.unitPrice || 0))}</span>
              ) },
            ]}
          />

          <div className="mt-3 flex justify-end">
            <div className="rounded-card border border-[var(--border-color)] bg-white/[0.03] p-3 text-sm">
              <div className="flex justify-between gap-6">
                <span className="text-[var(--text-secondary)]">Subtotal</span>
                <span className="font-bold">₹{formatINR(totals.subtotal)}</span>
              </div>
              <div className="flex justify-between gap-6">
                <span className="text-[var(--text-secondary)]">GST</span>
                <span className="font-bold">₹{formatINR(totals.gstAmount)}</span>
              </div>
              <div className="mt-1 flex justify-between gap-6 border-t border-[var(--border-color)] pt-1.5 text-base">
                <span className="font-extrabold text-[var(--text-primary)]">Grand Total</span>
                <span className="font-black text-accent">₹{formatINR(totals.grandTotal)}</span>
              </div>
            </div>
          </div>

          <p className="mt-2 text-[11px] text-[var(--text-muted)]">
            These lines become a <span className="font-semibold text-[var(--text-primary)]">Draft PO</span> —
            you can still edit them again on the PO before sending it to the vendor.
          </p>
        </>
      )}
    </Modal>
  );
}

function Meta({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">{label}</div>
      <div className="truncate text-xs font-semibold text-[var(--text-primary)]">{children}</div>
    </div>
  );
}
